import { PublishedStatusTypes, RolePermissions, RouteNames } from "enums";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { connect, type ConnectedProps } from "react-redux";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router";
import { campaignsStateSelector } from "../state/selectors";
import { useWizardStepsManager } from "hooks/useWizardStepsManager";
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { bindAction, type EnumDictionary, type ISegment } from "interfaces";
import ModalManager from "utils/ModalManager";
import ModalTypes from "components/modal/ModalTypes";
import Observable from "utils/Observable";
import { useRtn } from "hooks/useRtn";
import {
  CampaignDiscardV2Success,
  CampaignLockV2Success,
  CampaignPublishV2Success,
} from "features/Application/services/realTimeNotification/events/library/libraryEvents";
import Restricted from "features/Application/Restricted";
import EditCampaignDetailsHeader from "./EditCampaignDetailsHeader";
import RevertConfirmationModalTs from "components/modal/RevertConfirmationModalTs";
import { Dimmer, Loader } from "semantic-ui-react";
import EditCampaignSubheaderContainer from "./EditCampaignSubheaderContainer";
import Segments from "components/navigation/segments/Segments";
import Configure from "../Details/Configure";
import EmailTemplate from "../Details/EmailTemplate";
import LandingPage from "../Details/LandingPage";
import FeedbackPage from "../Details/FeedbackPage";
import { tryPublish } from "../state/thunks/campaignValidationThunk";
import { reset as resetValidation, discard as discardValidation } from "../state/slices/campaignValidationSlice";
import { clearCampaign } from "../state/slices/campaignDetailsSlice";
import { clearEmailTemplate } from "../state/slices/emailTemplateSlice";
import { clearLandingPage } from "../state/slices/landingPageSlice";
import { clearFeedbackPage } from "../state/slices/feedbackPageSlice";
import { reset as resetOverview } from "../state/slices/campaignOverviewSlice";
import {
  resetAppliedFilter,
  resetPagination,
  resetSearch,
  resetShouldSort,
  resetSortingColumnName,
  resetSortingDirection,
} from "../state/slices/campaignFiltersSlice";
import { getCampaign } from "../state/thunks/campaignDetailsThunk";
import { getEmailTemplate, getEmailTemplateContent } from "../state/thunks/emailTemplateThunk";
import { getLandingPageContent } from "../state/thunks/landingPageThunk";
import { getFeedbackPage } from "../state/thunks/feedbackPageThunk";
import { discardCampaign } from "../state/thunks/campaignEntityStateThunk";
import { resetState } from "../state/slices/campaignEntityStateSlice";
import { resetTags } from "../state/slices/campaignTagsSlice";
import { reset as resetSends } from "../state/slices/campaignSendsOverviewSlice";
import Sends from "../Details/Sends";
import Performance from "../Details/Reporting/ThreatDefensePerformance";
import cn from "classnames";
import styles from "./editCampaign.module.scss";

type EditCampaignPropsAll = PropsFromRedux;

enum EditSteps {
  Performance,
  Configuration,
  EmailTemplate,
  LandingPage,
  FeedbackPage,
  Sends,
}

const rtnEvents = [CampaignPublishV2Success, CampaignDiscardV2Success, CampaignLockV2Success];

const campaignsOverviewUrl = `/${RouteNames.contentSimulatedPhishingCampaigns}`;

const EditCampaign = (props: EditCampaignPropsAll) => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const campaignId = Number(params.id);
  const {
    getCampaign,
    getEmailTemplate,
    getEmailTemplateContent,
    getLandingPageContent,
    getFeedbackPage,
    discardCampaign,
    campaign,
    campaignValidation: { emailTemplateValidation, landingPageValidation, feedbackPageValidation },
    resetSearchFilters,
    onDiscard,
  } = props;
  const isPublishedAndNotPurchased = !props.campaign.isDraft && !props.campaign.isPurchased;

  const [isValid, setIsValid] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  const pageIndexes = [
    EditSteps.Performance,
    EditSteps.Configuration,
    EditSteps.EmailTemplate,
    EditSteps.LandingPage,
    EditSteps.FeedbackPage,
    EditSteps.Sends,
  ];

  const [wizardPages, { goToPage, goBack }, subscribeOnActiveIndexChanged] = useWizardStepsManager(pageIndexes);
  const pages: EnumDictionary<EditSteps, ISegment> = useMemo(() => {
    return {
      [EditSteps.Performance]: {
        to: "",
        label: "Performance",
        onClick: () => {
          goToPage(EditSteps.Performance);
        },
      },
      [EditSteps.Configuration]: {
        to: "configuration",
        label: "Configuration",
        onClick: () => {
          goToPage(EditSteps.Configuration);
        },
      },
      [EditSteps.EmailTemplate]: {
        to: "email",
        label: "Email",
        onClick: () => {
          goToPage(EditSteps.EmailTemplate);
        },
      },
      [EditSteps.LandingPage]: {
        to: "landing-page",
        label: "Landing Page",
        onClick: () => {
          goToPage(EditSteps.LandingPage);
        },
      },
      [EditSteps.FeedbackPage]: {
        to: "feedback-page",
        label: "Feedback Page",
        onClick: () => {
          goToPage(EditSteps.FeedbackPage);
        },
      },
      [EditSteps.Sends]: {
        to: "sends",
        label: "Sends",
        onClick: () => {
          goToPage(EditSteps.Sends);
        },
      },
    };
  }, [goToPage]);

  useLayoutEffect(() => {
    const handler = (_: any, activeStepIndex: EditSteps) => {
      pages[activeStepIndex].init?.();
      setActiveTab(activeStepIndex);
    };
    return subscribeOnActiveIndexChanged(handler);
  }, [pages, subscribeOnActiveIndexChanged]);

  const revertModalManager = useMemo(
    () => new ModalManager(ModalTypes.RevertCampaign, () => discardCampaign(campaignId), false, props.userId),
    [campaignId, props.userId, discardCampaign],
  );

  const onDiscardedObserver = useMemo(() => new Observable<() => void>(), []);

  useEffect(() => {
    return () => {
      if (!window.location.pathname.includes(campaignsOverviewUrl)) resetSearchFilters();
    };
  }, [resetSearchFilters]);

  const loadCampaignInfo = () => {
    getCampaign(campaignId);
  };

  const loadEmailTemplate = () => {
    if (!props.isEmailLoaded) {
      getEmailTemplate(campaignId);
    }
    if (!props.isEmailContentLoaded) {
      getEmailTemplateContent(campaignId);
    }
  };

  const loadLandingPage = () => {
    if (!props.isLandingPageLoaded) {
      getLandingPageContent(campaignId);
    }
  };

  const loadFeedbackPage = () => {
    if (!props.isFeedbackPageLoaded) {
      getFeedbackPage(campaignId);
    }
  };

  const loadCampaignDetails = () => {
    loadCampaignInfo();
    loadEmailTemplate();
    loadLandingPage();
    loadFeedbackPage();
  };

  useEffect(() => {
    loadCampaignDetails();
    return () => {
      props.onUnmount();
      revertModalManager.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPublishedOrDiscardEvent = useCallback(() => {
    onDiscardedObserver.notify();
    onDiscard(campaignId);
  }, [onDiscardedObserver, onDiscard, campaignId]);

  useRtn(rtnEvents, onPublishedOrDiscardEvent);

  const onErrorClick = () => {
    const validations = [
      { resolved: emailTemplateValidation.resolved, step: EditSteps.EmailTemplate },
      { resolved: landingPageValidation.resolved, step: EditSteps.LandingPage },
      { resolved: feedbackPageValidation.resolved, step: EditSteps.FeedbackPage },
    ];

    const unresolvedValidation = validations.find((validation) => !validation.resolved);

    if (unresolvedValidation) {
      const redirectTo = `${campaignsOverviewUrl}/${campaignId}/${pages[unresolvedValidation.step].to}`;
      if (!location.pathname.includes(redirectTo)) {
        navigate(redirectTo);
        goToPage(unresolvedValidation.step);
      }
    }
  };

  const onRevertClick = () => revertModalManager.execute();

  const renderRevertModal = () =>
    campaign.hasBeenPublished && <RevertConfirmationModalTs modalManager={revertModalManager} />;

  const onPublishClick = () => {
    props.tryPublish(campaignId);
  };

  const back = () => {
    if (!goBack()) {
      return campaignsOverviewUrl;
    }
  };

  const hasActiveErrors =
    !emailTemplateValidation.resolved || !landingPageValidation.resolved || !feedbackPageValidation.resolved;

  const isSendsTab = activeTab === EditSteps.Sends;
  const isPerformanceTab = activeTab === EditSteps.Performance;

  const isSendDisabled = campaign.isDraft;

  return (
    <Restricted
      permissions={[RolePermissions.CampaignsManage]}
      renderContent={(hasPermission: boolean) => (
        <div className="nested-content edit-campaign scrollable-content">
          <EditCampaignDetailsHeader
            campaignId={campaignId}
            campaignsOverviewUrl={campaignsOverviewUrl}
            title={campaign.name}
            canBeEdited={isPublishedAndNotPurchased}
            crudPermission={hasPermission}
            canBePublished={campaign.isDraft && !hasActiveErrors && !props.isUploadingFeedbackPageVideo}
            isRevertVisible={campaign.hasBeenPublished}
            invalidFormDetails={!isValid}
            publishedStatus={PublishedStatusTypes.ConvertToPublishedStatusType(
              !campaign.isDraft,
              campaign.hasBeenPublished,
            )}
            hideEntityStateButtons={isSendsTab}
            onErrorClick={onErrorClick}
            entityStateActions={{
              onPublish: onPublishClick,
              onRevert: onRevertClick,
            }}
            back={back}
            isSendsTab={isSendsTab}
            isSendDisabled={isSendDisabled}
          />
          {renderRevertModal()}
          <Dimmer active={props.isLoading} inverted>
            <Loader />
          </Dimmer>
          <EditCampaignSubheaderContainer
            publishedStatus={PublishedStatusTypes.ConvertToPublishedStatusType(!campaign.isDraft)}
            isUpdateInProgress={campaign.isDraft}
            lastModifiedDateTime={campaign.dateModified}
            showLastModified={!isSendsTab && !isPerformanceTab}
            showReportLastUpdated={isPerformanceTab}
          >
            <Segments to={`${campaignsOverviewUrl}/${campaignId}`}>
              <Segments.Segment {...pages[EditSteps.Performance]} />
              <Segments.Segment {...pages[EditSteps.Configuration]} />
              <Segments.Segment {...pages[EditSteps.EmailTemplate]} />
              <Segments.Segment {...pages[EditSteps.LandingPage]} />
              <Segments.Segment {...pages[EditSteps.FeedbackPage]} />
              <Segments.Segment {...pages[EditSteps.Sends]} />
            </Segments>
          </EditCampaignSubheaderContainer>
          <div className="scrollable-content" key={activeTab}>
            <Routes>
              <Route path={"/"} element={<Performance campaignId={campaignId} campaignName={campaign.name} />} />
              <Route
                path={pages[EditSteps.Configuration].to}
                element={
                  <div className="edit-form">
                    <Configure
                      entityId={campaignId}
                      acceptHandlers={wizardPages[EditSteps.Configuration]}
                      subscribeOnDiscarded={onDiscardedObserver.subscribe}
                      unSubscribeOnDiscarded={onDiscardedObserver.unsubscribe}
                      disabled={!campaign.isDraft}
                      onIsValidChange={setIsValid}
                      isReadOnlyView={campaign.isPurchased}
                    />
                  </div>
                }
              />
              <Route
                path={pages[EditSteps.EmailTemplate].to}
                element={
                  <div className={cn("edit-form", styles["edit-template"])}>
                    <EmailTemplate
                      isEdit
                      entityId={campaignId}
                      acceptHandlers={wizardPages[EditSteps.EmailTemplate]}
                      subscribeOnDiscarded={onDiscardedObserver.subscribe}
                      unSubscribeOnDiscarded={onDiscardedObserver.unsubscribe}
                      disabled={!campaign.isDraft}
                    />
                  </div>
                }
              />
              <Route
                path={pages[EditSteps.LandingPage].to}
                element={
                  <div className="edit-form">
                    <LandingPage
                      entityId={campaignId}
                      acceptHandlers={wizardPages[EditSteps.LandingPage]}
                      subscribeOnDiscarded={onDiscardedObserver.subscribe}
                      unSubscribeOnDiscarded={onDiscardedObserver.unsubscribe}
                      disabled={!campaign.isDraft}
                    />
                  </div>
                }
              />
              <Route
                path={pages[EditSteps.FeedbackPage].to}
                element={
                  <div className="edit-form">
                    <FeedbackPage
                      entityId={campaignId}
                      acceptHandlers={wizardPages[EditSteps.FeedbackPage]}
                      subscribeOnDiscarded={onDiscardedObserver.subscribe}
                      unSubscribeOnDiscarded={onDiscardedObserver.unsubscribe}
                      disabled={!campaign.isDraft}
                    />
                  </div>
                }
              />
              <Route
                path={pages[EditSteps.Sends].to}
                element={
                  <Sends
                    campaignId={campaignId}
                    acceptHandlers={wizardPages[EditSteps.Sends]}
                    isSendDisabled={isSendDisabled}
                  />
                }
              />
              <Route path="*" element={<Navigate to="../" replace />} />
            </Routes>
          </div>
        </div>
      )}
    />
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const base = campaignsStateSelector(state).base;

  return {
    campaign: base.details.campaign,
    isLoading: base.details.isLoading,
    isCampaignLoaded: base.details.isLoaded,
    isEmailLoaded: base.emailTemplate.isLoaded,
    isEmailContentLoaded: base.emailTemplate.isContentLoaded,
    isLandingPageLoaded: base.landingPage.isLoaded,
    isFeedbackPageLoaded: base.feedbackPage.isLoaded,
    isUploadingFeedbackPageVideo: base.feedbackPage.isUploadingVideo,
    campaignValidation: base.campaignValidation,
    userId: Number(state.userProfile.id),
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getCampaign: bindAction(getCampaign, dispatch),
  getEmailTemplate: bindAction(getEmailTemplate, dispatch),
  getEmailTemplateContent: bindAction(getEmailTemplateContent, dispatch),
  getLandingPageContent: bindAction(getLandingPageContent, dispatch),
  getFeedbackPage: bindAction(getFeedbackPage, dispatch),
  tryPublish: bindAction(tryPublish, dispatch),
  discardCampaign: bindAction(discardCampaign, dispatch),
  onDiscard: (id: number) => {
    dispatch(discardValidation());
    dispatch(getCampaign(id));
    dispatch(getEmailTemplate(id));
    dispatch(getEmailTemplateContent(id));
    dispatch(getLandingPageContent(id));
    dispatch(getFeedbackPage(id));
  },
  onUnmount: () => {
    dispatch(clearCampaign());
    dispatch(clearEmailTemplate());
    dispatch(clearLandingPage());
    dispatch(clearFeedbackPage());
    dispatch(resetValidation());
    dispatch(resetState());
    dispatch(resetTags());
    dispatch(resetSends());
  },
  resetSearchFilters: () => {
    dispatch(resetOverview());
    dispatch(resetSearch());
    dispatch(resetAppliedFilter());
    dispatch(resetPagination());
    dispatch(resetSortingColumnName());
    dispatch(resetSortingDirection());
    dispatch(resetShouldSort());
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(EditCampaign);
export default ConnectedComponent;
